'use client';
import { motion } from 'framer-motion';
import { useDesign } from '@/context/DesignContext';
import { getImageUrl } from '@/lib/utils';

interface RestaurantProductsProps {
  title?: string;
  subtitle?: string;
  products?: Array<{
    id: number;
    name: string;
    description?: string | null;
    price: number;
    stock: number;
    image?: string | null;
    category?: { id: number; name: string } | null;
  }>;
  design?: any;
}

export default function RestaurantProducts({
  title = 'Our Specialties',
  subtitle = 'Crafted with passion, served with pride',
  products = [],
  design: propDesign
}: RestaurantProductsProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  if (!products || products.length === 0) return null;

  const primaryColor = design?.colors?.primary || '#E07A5F';
  const secondaryColor = design?.colors?.secondary || '#3D405B';
  const textColor = design?.colors?.text || '#1E1E1E';
  const textLight = design?.colors?.textLight || '#6B6B6B';
  const borderColor = design?.colors?.border || '#E8E4DA';
  const bgColor = design?.colors?.background || '#FFFFFF';
  const headingFont = design?.typography?.headingFont || "'Cormorant Garamond', serif";
  const bodyFont = design?.typography?.bodyFont || "'Inter', sans-serif";
  const borderRadius = design?.effects?.borderRadius || '12px';
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  return (
    <section className="restaurant-products py-20" style={{ background: bgColor }}>
      <div className="container mx-auto px-6" style={{ maxWidth: containerMaxWidth }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: headingFont, color: textColor }}>
            {title}
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ fontFamily: bodyFont, color: textLight }}>{subtitle}</p>
          <div className="w-20 h-1 mx-auto mt-4" style={{ background: primaryColor }} />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden border transition-all duration-300 hover:shadow-xl"
              style={{ borderColor: borderColor }}
            >
              <div className="aspect-square overflow-hidden relative" style={{ background: '#f8f5f0' }}>
                {product.image ? (
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-7xl" style={{ color: textLight }}>
                    🍽️
                  </div>
                )}
                {product.stock !== undefined && product.stock <= 0 && (
                  <div className="absolute top-4 right-4 px-3 py-1 text-xs font-semibold rounded-full bg-red-500 text-white">
                    Rupture
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-serif text-xl font-bold" style={{ fontFamily: headingFont, color: textColor }}>
                    {product.name}
                  </h3>
                  <span className="font-serif text-lg font-bold" style={{ color: primaryColor }}>
                    {product.price} TND
                  </span>
                </div>
                {product.category && (
                  <p className="text-sm mb-2" style={{ color: textLight, fontFamily: bodyFont }}>
                    {product.category.name}
                  </p>
                )}
                {product.description && (
                  <p className="text-sm mb-4 line-clamp-2" style={{ color: textLight, fontFamily: bodyFont, lineHeight: 1.6 }}>
                    {product.description}
                  </p>
                )}
                <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: borderColor }}>
                  <span className="text-xs" style={{ color: textLight, fontFamily: bodyFont }}>
                    {product.stock > 0 ? `✓ En stock (${product.stock})` : '✕ Rupture'}
                  </span>
                  <button
                    className={`px-5 py-2 text-sm font-medium rounded-full transition-colors ${product.stock <= 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-85'}`}
                    style={{
                      background: primaryColor,
                      color: '#FFFFFF',
                      fontFamily: bodyFont,
                      cursor: product.stock <= 0 ? 'not-allowed' : 'pointer',
                    }}
                    disabled={product.stock <= 0}
                  >
                    Commander
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}